import { Star } from "lucide-react";
import type { TrustConfig } from "@autopic/shared";
import styles from "./TrustStrip.module.css";

interface TrustStripProps {
  trust: TrustConfig;
}

/**
 * Trustpilot-style trust strip shown near the decision points. Renders 5 stars
 * with a partial fill for the fractional rating, plus the review count.
 */
export function TrustStrip({ trust }: TrustStripProps) {
  if (!trust.enabled) return null;

  const rating = trust.rating ?? 4.6;
  const label = trust.label ?? ratingWord(rating);
  const count = trust.reviewCount;

  return (
    <div className={styles.strip}>
      <span className={styles.word}>{label}</span>
      <span className={styles.stars} aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => {
          const fill = Math.max(0, Math.min(1, rating - i));
          return (
            <span key={i} className={styles.starBox}>
              <Star size={16} className={styles.starBg} />
              <span className={styles.starClip} style={{ width: `${fill * 100}%` }}>
                <Star size={16} className={styles.starFg} />
              </span>
            </span>
          );
        })}
      </span>
      {typeof count === "number" && (
        <span className={styles.count}>
          {count.toLocaleString("en-GB")} reviews
        </span>
      )}
      <span className={styles.provider}>on Trustpilot</span>
      <span className="ap-sr-only">
        Rated {label}, {rating} out of 5{count ? ` from ${count} reviews` : ""} on Trustpilot
      </span>
    </div>
  );
}

function ratingWord(r: number): string {
  if (r >= 4.8) return "Excellent";
  if (r >= 4.3) return "Great";
  if (r >= 3.5) return "Good";
  return "Average";
}
